// src/context/AuthContext.jsx
import { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirectAfterAuth, setRedirectAfterAuth] = useState(null);
  const [setAlert] = useState({ message: "", type: "", visible: false });

  const navigate = useNavigate();

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const showAlert = (message, type = "success") => {
    setAlert({ message, type, visible: true });
    setTimeout(() => setAlert({ message: "", type: "", visible: false }), 3000);
  };

  useEffect(() => {
    const tryRestoreSession = async () => {
      try {
          // If no user in /auth/me, try /users/profile
          const profileRes = await axios.get(`${BASE_URL}/api/users/profile`, {
            withCredentials: true,
          });
          setUser(profileRes.data);
        }
      catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    tryRestoreSession();
  }, [BASE_URL]);

  const login = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/users/profile`, {
        withCredentials: true,
      });

      const loggedInUser = res.data;

      if (loggedInUser.status !== "active") {
        showAlert("Your account has been suspended. Please contact support.", "error");

        await logout(); // clears cookie and resets context
        navigate("/signin");
        return;
      }

      setUser(loggedInUser);
    } catch {
      setUser(null);
    }
  };

  const register = async (formData) => {
    try {
      const res = await axios.post(`${BASE_URL}/api/users/register`, formData, {
        withCredentials: true,
      });

      // ✅ Check if the registered user is an admin
      if (res.data?.isAdmin) {
        const profile = await axios.get(`${BASE_URL}/api/users/profile`, {
          withCredentials: true,
        });
        setUser(profile.data);
      } else {
        // ✅ For regular users, do NOT log them in automatically
        setUser(null);
      }

      return res.data; // Return response so UI can show success message
    } catch (err) {
      console.error("Registration failed:", err);
      setUser(null);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BASE_URL}/api/users/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setUser(null);
      navigate("/signin");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        loading,
        redirectAfterAuth,
        setRedirectAfterAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
